import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug, 2) : [];

  if (!article) {
    return <NotFound />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `https://caodenghoshang.com/article/${article.slug}#article`,
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.coverImage,
        inLanguage: 'zh-Hans',
        isPartOf: { '@id': 'https://caodenghoshang.com/#website' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://caodenghoshang.com/' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://caodenghoshang.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <article>
          <header className="pt-28 pb-10 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
                  {article.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 truncate">{article.title}</span>
              </nav>

              <div className="flex items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="text-foreground-300">·</span>
                <span>{article.date}</span>
              </div>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-700 leading-relaxed
                prose-p:mb-5 prose-p:text-foreground-700
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:font-heading prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-3
                [&_br]:hidden"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-secondary-200/70">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {related.map((r) => (
                  <ArticleCard key={r.slug} article={r} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}